import { CodeLinkType, CollaborationToolType, InterviewMeetingLocationType } from 'src/generated/mloop-graphql';

import {
  CodingUrlBySlotId,
  CustomVideoMeetingLinkUrlBySlotId,
  InterviewMeetingLocationIdBySlotId,
  MeetingLinksConfig,
  MeetingRoomSuggestionInterface,
  ScheduleCommunicationsState,
  ScheduleContent,
  VideoMeetingLinkBySlotId,
  VideoMeetingUserIdBySlotId,
  ZoomInfo,
} from 'src/store/slices/schedule-communications';

import { AdditionalAttendeesByInterviewId, InterviewAdditionalAttendees } from 'src/utils/additionalAttendees';

import { EMPTY_OBJECT } from 'src/constants';
import { State } from 'src/store';

export const getCommonMeetingRoomSuggestionsByEventId = (
  state: State,
  scheduleId: string,
  eventId: string
): MeetingRoomSuggestionInterface[] | undefined =>
  state.scheduleCommunications.byId[scheduleId]?.commonMeetingRoomSuggestionByEvent[eventId];

export const getMeetingRoomSuggestionsByInterviewEventId = (
  state: State,
  scheduleId: string,
  interviewEventId: string
): MeetingRoomSuggestionInterface[] | undefined =>
  state.scheduleCommunications.byId[scheduleId]?.meetingRoomSuggestionsPerInterviewEvent[interviewEventId];

export const getMeetingRoomUpdates = (
  state: State,
  scheduleId: string,
  interviewEventId: string
): {
  addedRooms: MeetingRoomSuggestionInterface[];
  deletedRooms: MeetingRoomSuggestionInterface[];
  unchangedRooms: MeetingRoomSuggestionInterface[];
} => {
  const added: MeetingRoomSuggestionInterface[] = [];
  const deleted: MeetingRoomSuggestionInterface[] = [];
  const unchanged: MeetingRoomSuggestionInterface[] = [];

  const { selectedScheduleId } = state.scheduling;
  const scheduleUpdates = selectedScheduleId ? state.scheduleUpdate.byId[selectedScheduleId] : null;

  if (scheduleUpdates) {
    scheduleUpdates.forEach((update) => {
      if (update.applicationStageInterviewId !== interviewEventId) {
        return;
      }

      if (update.type === 'ScheduleUpdateAddInterviewMeetingRoom') {
        added.push(update.roomSuggestion);
      } else if (update.type === 'ScheduleUpdateDeleteInterviewMeetingRoom') {
        deleted.push(update.roomSuggestion);
      }
    });
  }

  const meetingRoomSuggestions =
    state.scheduleCommunications.byId[scheduleId]?.meetingRoomSuggestionsPerInterviewEvent[interviewEventId];

  if (meetingRoomSuggestions) {
    meetingRoomSuggestions.forEach((roomSuggestion) => {
      if (
        added.findIndex((suggestion) => suggestion.room?.id === roomSuggestion.room?.id) === -1 &&
        deleted.findIndex((suggestion) => suggestion.room?.id === roomSuggestion.room?.id) === -1
      ) {
        unchanged.push(roomSuggestion);
      }
    });
  }

  return { addedRooms: added, deletedRooms: deleted, unchangedRooms: unchanged };
};

export const getApplicationStageIdByScheduleId = (state: State, scheduleId: string): string | undefined =>
  state.scheduleCommunications.byId[scheduleId]?.applicationStageId;

export const getAddedCodeLinkTypes = (state: State, scheduleId: string): CodeLinkType[] => {
  const codingUrlByInterviewEvents = state.scheduleCommunications.byId[scheduleId]?.codingUrlBySlotId;
  const codeLinkTypes: CodeLinkType[] = [];

  if (!codingUrlByInterviewEvents) {
    return codeLinkTypes;
  }

  Object.keys(codingUrlByInterviewEvents).forEach((codeLinkType: CodeLinkType) => codeLinkTypes.push(codeLinkType));

  return codeLinkTypes;
};

export const getAddedCollaborationLinkType = (state: State, scheduleId: string): CollaborationToolType | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.collaborationLinkType;
};

export const getAllCodingUrls = (state: State, scheduleId: string): CodingUrlBySlotId | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.codingUrlBySlotId;
};

export const getCodingUrl = (
  state: State,
  scheduleId: string,
  interviewEventId: string,
  codeLinkType: CodeLinkType
): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.codingUrlBySlotId[codeLinkType]?.[interviewEventId];
};

export const getCollaborationTool = (state: State, scheduleId: string) => {
  return state.scheduleCommunications.byId[scheduleId]?.collaborationToolBySlotId;
};

export const getCollaborationToolBySlotId = (
  state: State,
  { scheduleId, slotId }: { scheduleId: string; slotId: string }
) => {
  return state.scheduleCommunications.byId[scheduleId]?.collaborationToolBySlotId[slotId];
};

export const getCustomVideoMeetingLinkUrlForSlotId = (
  state: State,
  scheduleId: string,
  slotId: string
): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.customVideoMeetingLinkUrls?.[slotId];
};
export const getCustomVideoMeetingLinkUrls = (
  state: State,
  scheduleId: string | null
): CustomVideoMeetingLinkUrlBySlotId => {
  if (!scheduleId) {
    return {};
  }
  return state.scheduleCommunications.byId[scheduleId]?.customVideoMeetingLinkUrls;
};

export const getMeetingLinksConfig = (state: State, scheduleId: string): MeetingLinksConfig | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.meetingLinkConfig;
};

export const getInterviewMeetingLocationId = (state: State, scheduleId: string): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.interviewMeetingLocationId;
};

export const getInterviewMeetingLocationIds = (
  state: State,
  scheduleId: string
): InterviewMeetingLocationIdBySlotId | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.interviewMeetingLocationIdBySlotId;
};

export const getCustomVideoMeetingLink = (state: State, scheduleId: string): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.customVideoMeetingLink;
};

export const getVideoMeetingLinkHostEmployeeId = (state: State, scheduleId: string | null): string | undefined => {
  if (!scheduleId) {
    return undefined;
  }
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingLinkHostEmployeeId;
};

export const getVideoMeetingLink = (state: State, scheduleId: string): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingLink;
};

export const getVideoMeetingLinkUrls = (state: State, scheduleId: string | null): VideoMeetingLinkBySlotId => {
  if (!scheduleId) {
    return {};
  }
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingLinkBySlotId;
};

export const getVideoMeetingLinkHostEmployees = (
  state: State,
  scheduleId: string | null
): VideoMeetingUserIdBySlotId => {
  if (!scheduleId) {
    return EMPTY_OBJECT;
  }
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingHostEmployeeIdBySlotId;
};

export const getVideoMeetingLinkBySlotId = (
  state: State,
  { scheduleId, slotId }: { scheduleId: string; slotId: string }
): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingLinkBySlotId[slotId];
};

export const getVideoMeetingLinkHostEmployeeIdBySlotId = (
  state: State,
  { scheduleId, slotId }: { scheduleId: string; slotId: string }
): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.videoMeetingHostEmployeeIdBySlotId[slotId];
};

export const getZoomUserId = (state: State, scheduleId: string): string | undefined =>
  state.scheduleCommunications.byId[scheduleId]?.zoomUserId;

export const getZoomInfo = (state: State, scheduleId: string): ZoomInfo | undefined =>
  state.scheduleCommunications.byId[scheduleId]?.zoomInfo;

export const getZoomInfos = (state: State, scheduleId: string) =>
  state.scheduleCommunications.byId[scheduleId]?.zoomInfos;

export const getZoomUserIds = (state: State, scheduleId: string) =>
  state.scheduleCommunications.byId[scheduleId]?.zoomUserIds;

export const getZoomUserIdBySlotId = (state: State, scheduleId: string, slotId: string): string | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.zoomUserIds[slotId];
};

export const getZoomInfoBySlotId = (state: State, scheduleId: string, slotId: string): ZoomInfo | undefined => {
  return state.scheduleCommunications.byId[scheduleId]?.zoomInfos[slotId];
};

export const getSlackChannelEnabled = (state: State, scheduleId: string | null): boolean => {
  if (!scheduleId) {
    return false;
  }
  // Wrapping in boolean
  return Boolean(state.scheduleCommunications.byId[scheduleId]?.slackChannelEnabled);
};

export const getSlackChannelTemplateId = (state: State, scheduleId: string | null): string | undefined => {
  if (!scheduleId) {
    return undefined;
  }
  return state.scheduleCommunications.byId[scheduleId]?.slackChannelMessageTemplateID;
};

export const getLocation = (state: State, scheduleId: string): InterviewMeetingLocationType => {
  return state.scheduleCommunications.byId[scheduleId]?.location || InterviewMeetingLocationType.None;
};

export const getDebriefRescheduleDetailHasAttendeeAdded = (state: State, scheduleId: string) => {
  return state.scheduleCommunications.byId[scheduleId]?.hasAttendeeAdded;
};

export const getDebriefRescheduleDetailHasScheduleTimeAfterDebriefStartAt = (state: State, scheduleId: string) => {
  return state.scheduleCommunications.byId[scheduleId]?.hasScheduleTimeAfterDebriefStartAt;
};

export const getScheduleContent = (state: State): ScheduleContent | null | undefined => {
  return state.scheduleCommunications.byId[state.scheduling.selectedScheduleId || '']?.scheduleContent;
};

/**
 * Every additional-attendee list this flow has drafted, keyed by the interview's slot (its
 * `jobStageInterview` id) instead of by the schedule event.
 *
 * Leaving the Setup step always re-fetches schedule options, and the server mints a new schedule id
 * and new event ids each time, so the whole draft is rebuilt under keys nothing else knows. The
 * slot id is the interview's stable identity across that regeneration, so it is what carries a
 * scheduler's recipient choices forward. Later schedules win, which favours the most recent draft.
 */
export const buildAdditionalAttendeeDraftsBySlotId = (
  byId: ScheduleCommunicationsState['byId']
): AdditionalAttendeesByInterviewId => {
  const bySlotId: AdditionalAttendeesByInterviewId = {};

  Object.values(byId ?? {}).forEach((communication) => {
    const content = communication?.scheduleContent;
    if (!content) {
      return;
    }
    content.interviewerEventContents?.forEach((eventContent) => {
      const attendees = content.interviewerAdditionalAttendeesByInterviewId?.[eventContent.event?.id];
      if (!eventContent.slotId || !attendees) {
        return;
      }
      bySlotId[eventContent.slotId] = attendees;
    });
  });

  return bySlotId;
};

/** One interviewer event's additional-attendee list, keyed by the interview's `event.id`. */
export const getInterviewerAdditionalAttendeesForInterview = (
  state: State,
  interviewId: string
): InterviewAdditionalAttendees | undefined => {
  return getScheduleContent(state)?.interviewerAdditionalAttendeesByInterviewId?.[interviewId];
};
